import { useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import client from 'apollo/client';
import dayjs from 'dayjs';

import { APPLY_SHORT_CYCLE, UPDATE_BILLING_CYCLE } from 'mutations/cycleDate';
import { ACCOUNT_CARD_ALERT } from 'mutations/sendCard';
import { formatDateToSubmitIsoUtcOffset } from 'utilities/datesAndTimes';
import useSubmitProgress from 'hooks/useSubmitProgress.jsx';
import {
  resetApplyStatusState,
  setMutationFailures,
  setShouldRunSubmit,
  useApplyStatusState,
} from './applyStatusState';
import useCustomerInfo from 'hooks/useCustomerInfo';

const getNewBillingCycleDay = () => {
  const today = dayjs().date();

  if ([28, 29, 30, 31, 1].includes(today)) {
    return 2;
  }

  return today + 1;
};

const SubmitCLV9StatusStep = ({
  setAccountFraudStatus,
  accountStatusMutation,
  closeModal,
}) => {
  const { customerInfo } = useCustomerInfo();

  const { shouldRunSubmit, accountFraudStatusVariables, mutationFailures } =
    useApplyStatusState();

  const [applyShortCycle, shortCycleMutation] = useMutation(APPLY_SHORT_CYCLE);

  const [updateBillingCycle, billingCycleMutation] =
    useMutation(UPDATE_BILLING_CYCLE);

  const [accountCardAlert, cardAlertMutation] = useMutation(ACCOUNT_CARD_ALERT);

  const submitProgressConfig = {
    steps: [
      {
        queries: [shortCycleMutation],
        text: 'Apply Short Cycle Assignment',
        errorMessage: 'Failed to apply account short cycle. Please retry.',
        errorTestId: 'short-cycle-mutation-failure-alert',
      },
      {
        queries: [billingCycleMutation],
        text: 'Update Billing Cycle',
        errorMessage:
          'Please perform full remediation checks in GreenScreens to complete the process.',
        disabled: mutationFailures,
      },
      {
        queries: [accountStatusMutation],
        text: 'Apply Account Status',
        errorMessage:
          'Please perform full remediation checks in GreenScreens to complete the process.',
        disabled: mutationFailures,
      },
      {
        queries: [cardAlertMutation],
        text: 'Security Report',
        errorMessage:
          'Please perform full remediation checks in GreenScreens to complete the process.',
        disabled: mutationFailures,
      },
    ],
    successMessage: `Account successfully closed with ${accountFraudStatusVariables?.reasonCode}`,
    successTestId: 'closed-app-fraud-success-message',
    onComplete: () => {
      setShouldRunSubmit(false);
      setTimeout(() => {
        resetApplyStatusState();
        closeModal();
      }, 2000);
    },
    onSubmit: async ({ markInitiated, markCompleted, hasSubmittedRef }) => {
      try {
        markInitiated();

        const now = formatDateToSubmitIsoUtcOffset(new Date());

        if (!shortCycleMutation?.data) {
          await applyShortCycle({
            variables: {
              accountId: accountFraudStatusVariables?.accountIds[0],
            },
          });
        }

        if (!shortCycleMutation?.error) {
          await updateBillingCycle({
            variables: {
              accountId: accountFraudStatusVariables?.accountIds[0],
              newBillingCycleDay: getNewBillingCycleDay(),
            },
          });
        }

        if (!billingCycleMutation?.error) {
          await setAccountFraudStatus({
            variables: accountFraudStatusVariables,
          });
        }

        if (!accountStatusMutation?.error) {
          await accountCardAlert({
            variables: {
              accountId: accountFraudStatusVariables?.accountIds[0],
              customerId: accountFraudStatusVariables?.customerId,
              reason: 'FA',
              dateTimeDiscovered: now,
              dateTimeReported: now,
              fraudOnAccount: false,
              isCardLost: true,
              isPinLost: true,
              lostStolenCircumstances: 'Application Fraud',
              reporter: customerInfo.fullName,
              willAddOverride: false,
            },
          });
        }

        markCompleted();
        setShouldRunSubmit(false);
        hasSubmittedRef.current = false;

        await client.refetchQueries({
          include: [
            'GetAccountStatusesRaw',
            'GetCustomerWithFullAccounts',
            'GetAccountStatementPreferences',
          ],
        });
      } catch (error) {
        console.error('Submit error:', error);
        throw error;
      }
    },
  };

  const {
    submissionInitiated,
    handleSubmit,
    renderProgress: renderSubmitProgress,
  } = useSubmitProgress(submitProgressConfig);

  useEffect(() => {
    if (shouldRunSubmit && !submissionInitiated) {
      handleSubmit();
    }
  }, [shouldRunSubmit, submissionInitiated, handleSubmit]);

  return <div>{submissionInitiated && renderSubmitProgress()}</div>;
};

export default SubmitCLV9StatusStep;
